<!--
  Reusable modal with header / content / footer slots.
  Usage (default slot for body):
    <BaseModal v-model="open">
      <template #header>Title</template>
      <p>Main content</p>
      <template #footer><button @click="open = false">Close</button></template>
    </BaseModal>
  Or use named #content for body:
    <BaseModal v-model="open">
      <template #header>Title</template>
      <template #content><p>Main content</p></template>
      <template #footer><button @click="open = false">Close</button></template>
    </BaseModal>
  - slideFromSide=true: desktop = slide from left/right (side prop).
  - bottomSheetOnMobile: when true, on mobile use bottom sheet (works for both centered and side-panel).
  - slideFromSide=false: centered on desktop; on mobile centered unless bottomSheetOnMobile=true.
-->
<template src="./template.html"></template>
<style lang="scss" src="./styles.scss"></style>

<script>
export default {
  name: 'BaseModal',
  inheritAttrs: false,
  props: {
    /** v-model: whether the modal is open */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * When true: desktop = slide from left/right (use `side` prop).
     * When false: standard centered overlay on all viewports.
     */
    slideFromSide: {
      type: Boolean,
      default: false,
    },
    /**
     * When true on mobile: open as bottom sheet (works for both centered and side-panel modals).
     * When false on mobile: centered stays centered, side-panel stays side panel.
     */
    bottomSheetOnMobile: {
      type: Boolean,
      default: false,
    },
    /** For desktop + slideFromSide: which side the panel comes from */
    side: {
      type: String,
      default: 'right',
      validator: (v) => ['left', 'right'].includes(v),
    },
    /** Whether clicking the backdrop closes the modal */
    closeOnBackdrop: {
      type: Boolean,
      default: true,
    },
    /** Whether to show the built-in close icon button in the header */
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    /** Optional width for the panel (e.g. '600px', '90%', '40rem'). Applied to centered panel. */
    width: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isMobile: false,
      modalPanel: null,
      mobileQuery: null,
      savedScrollY: 0,
      hasBeenOpen: false,
      savedBodyPaddingRight: '',
      savedHtmlPaddingRight: '',
      previousActiveElement: null,
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    /** Desktop: slide from left/right; or mobile when slideFromSide but not bottomSheetOnMobile */
    isSidePanel() {
      return this.slideFromSide && (!this.isMobile || !this.bottomSheetOnMobile)
    },
    /** Mobile: bottom sheet when bottomSheetOnMobile (works for both centered and side-panel) */
    isBottomSheet() {
      return this.isMobile && this.bottomSheetOnMobile
    },
    /** Centered modal: desktop when !slideFromSide; mobile when !slideFromSide and !bottomSheetOnMobile */
    isCentered() {
      return !this.slideFromSide && (!this.isMobile || !this.bottomSheetOnMobile)
    },
    panelClass() {
      if (this.isCentered) return 'base-modal--center'
      if (this.isBottomSheet) return 'base-modal--bottom-sheet'
      return this.side === 'left' ? 'base-modal--side-left' : 'base-modal--side-right'
    },
    panelStyle() {
      if (!this.width || !this.isCentered) return null
      const w = typeof this.width === 'number' ? `${this.width}px` : String(this.width)
      return { width: w, maxWidth: w }
    },
  },
  watch: {
    isOpen: {
      async handler(open) {
        if (open) {
          this.hasBeenOpen = true
          this.savedScrollY = window.scrollY ?? window.pageYOffset
          const scrollbarWidth = this.getScrollbarWidth()
          this.savedBodyPaddingRight = document.body.style.paddingRight
          this.savedHtmlPaddingRight = document.documentElement.style.paddingRight
          if (scrollbarWidth > 0) {
            const bodyCurrent = parseFloat(getComputedStyle(document.body).paddingRight) || 0
            const htmlCurrent = parseFloat(getComputedStyle(document.documentElement).paddingRight) || 0
            document.body.style.paddingRight = `${bodyCurrent + scrollbarWidth}px`
            document.documentElement.style.paddingRight = `${htmlCurrent + scrollbarWidth}px`
          }
          document.documentElement.classList.add('base-modal-active')
          document.body.classList.add('base-modal-active')
          document.addEventListener('keydown', this.onEscape)

          this.previousActiveElement = document.activeElement

          await this.$nextTick()

          this.focusFirstElement()
          window.scrollTo(0, this.savedScrollY)
        } else {
          document.body.style.paddingRight = this.savedBodyPaddingRight ?? ''
          document.documentElement.style.paddingRight = this.savedHtmlPaddingRight ?? ''
          this.savedBodyPaddingRight = ''
          this.savedHtmlPaddingRight = ''
          document.documentElement.classList.remove('base-modal-active')
          document.body.classList.remove('base-modal-active')
          document.removeEventListener('keydown', this.onEscape)

          this.previousActiveElement?.focus()

          if (this.hasBeenOpen) {
            window.scrollTo(0, this.savedScrollY)
            this.hasBeenOpen = false
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const mq = window.matchMedia('(max-width: 767px)')
    this.mobileQuery = mq
    this.isMobile = mq.matches
    mq.addEventListener('change', this.onBreakpointChange)
  },
  beforeUnmount() {
    document.body.style.paddingRight = this.savedBodyPaddingRight ?? ''
    document.documentElement.style.paddingRight = this.savedHtmlPaddingRight ?? ''
    document.documentElement.classList.remove('base-modal-active')
    document.body.classList.remove('base-modal-active')
    document.removeEventListener('keydown', this.onEscape)
    if (this.isOpen) {
      window.scrollTo(0, this.savedScrollY)
    }
    if (this.mobileQuery) {
      this.mobileQuery.removeEventListener('change', this.onBreakpointChange)
    }
  },
  methods: {
    getScrollbarWidth() {
      const fromViewport = Math.max(0, window.innerWidth - document.documentElement.clientWidth)
      if (fromViewport > 0) return fromViewport
      const div = document.createElement('div')
      div.style.cssText = 'overflow:scroll;position:absolute;width:100px;height:100px;visibility:hidden'
      document.body.appendChild(div)
      const width = div.offsetWidth - div.clientWidth
      document.body.removeChild(div)
      if (width > 0) return width
      return 0
    },
    onBreakpointChange(e) {
      this.isMobile = e.matches
    },
    onEscape(e) {
      if (e.key === 'Escape') this.close()
    },
    close() {
      this.isOpen = false
    },
    onBackdropClick() {
      if (this.closeOnBackdrop) this.close()
    },
    focusFirstElement() {
      const container = this.$refs.modalPanel
      if (!container) return

      const el = container.querySelector(
        'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])'
      )

      if (el) {
        el.focus()
      } else {
        container.focus()
      }
    },
    handleTab(e) {
      if (e.key !== 'Tab') return

      const container = this.$refs.modalPanel
      const focusable = container.querySelectorAll(
        'input, select, textarea, button, a[href], [tabindex]:not([tabindex="-1"])'
      )

      if (!focusable.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
  },
}
</script>
